/* TPS — ürün video şeridi <tps-video-reel> (snippets/tps-video-item.liquid, main-product › tps_video blokları).
   Ardışık tps_video blokları tek şerit; grupta tek native <dialog>, aktif video değişir. Önceki/sonraki ve ←/→
   yalnız videosu dolu öğeler ([data-video-item]) arasında gezer (editördeki boş yer tutucular atlanır). Kaynaklar yalnız açılışta <template data-sources>'tan kopyalanır,
   kapanışta boşaltılır (indirme durur). Tema yardımcıları (global.js: trapFocus, removeTrapFocus,
   setScrollbarWidth, pauseAllMedia) varsa kullanılır, yoksa sessizce atlanır. */
(() => {
  if (customElements.get('tps-video-reel')) return;

  const call = (name, ...args) => (typeof window[name] === 'function' ? window[name](...args) : undefined);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class TpsVideoReel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return; // viewTransition sırasında iki kez bağlanabilir
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.video = this.dialog.querySelector('[data-video]');
      this.items = Array.from(this.querySelectorAll('.tps-video-reel__item[data-video-item]'));
      this.status = this.dialog.querySelector('[data-status]');
      this.soundButton = this.dialog.querySelector('[data-sound-toggle]');
      this.playButton = this.dialog.querySelector('[data-play-toggle]');
      this.muted = this.dataset.sound !== 'true';

      this.addEventListener('click', (event) => {
        const card = event.target.closest('[data-open]');
        if (!card || this.dialog.contains(card)) return;
        const index = this.items.indexOf(card.closest('[data-video-item]'));
        if (index > -1) this.open(index, card);
      });
      this.dialog.addEventListener('click', (event) => this.onDialogClick(event));
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event));
      // Esc: animasyonlu kapanış. Tarayıcı cancel'ı atlarsa 'close' yine temizler.
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => this.cleanup());
      this.video.addEventListener('play', () => this.syncPlay());
      this.video.addEventListener('pause', () => this.syncPlay());
    }

    disconnectedCallback() {
      if (this.dialog && this.dialog.open) this.dialog.close();
      this.cleanup();
    }

    open(index, opener) {
      this.opener = opener;
      this.isOpen = true;
      call('pauseAllMedia');
      call('setScrollbarWidth');
      this.locked = !document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      this.dialog.classList.remove('is-closing');
      if (!this.dialog.open) this.dialog.showModal();
      this.show(index);
      const closeButton = this.dialog.querySelector('[data-close]');
      if (typeof window.trapFocus === 'function') window.trapFocus(this.dialog, closeButton);
      else closeButton.focus();
    }

    show(index) {
      const count = this.items.length;
      this.index = (index + count) % count;
      const item = this.items[this.index];
      const template = item.querySelector('template[data-sources]');
      const video = this.video;
      video.pause();
      video.replaceChildren(template ? template.content.cloneNode(true) : '');
      if (item.dataset.poster) video.poster = item.dataset.poster;
      else video.removeAttribute('poster');
      this.dialog.style.setProperty('--tps-reel-ratio', item.dataset.ratio || '0.5625');
      this.dialog.setAttribute('aria-label', `${this.dataset.label}: ${item.dataset.title || ''}`);
      video.load();
      if (count > 1) this.announce(this.dataset.counter.replace('[c]', this.index + 1).replace('[t]', count));
      this.play();
    }

    play() {
      const video = this.video;
      video.muted = this.muted;
      this.syncSound();
      const played = video.play();
      if (!played || typeof played.catch !== 'function') return;
      played.catch((error) => {
        // Yalnız sesli otomatik oynatma reddinde (NotAllowedError) sessize düşüp tekrar dene.
        if (!error || error.name !== 'NotAllowedError' || video.muted) return this.syncPlay();
        this.muted = true;
        video.muted = true;
        this.syncSound();
        video.play().catch(() => this.syncPlay());
      });
    }

    close() {
      if (!this.dialog.open || this.closing) return;
      if (reducedMotion()) return this.dialog.close();
      this.closing = true;
      this.dialog.classList.add('is-closing');
      let timer;
      const done = () => {
        clearTimeout(timer);
        this.dialog.removeEventListener('animationend', done);
        if (this.dialog.open) this.dialog.close();
      };
      timer = setTimeout(done, 320);
      this.dialog.addEventListener('animationend', done);
    }

    cleanup() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.closing = false;
      this.dialog.classList.remove('is-closing');
      const video = this.video;
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {}
      video.replaceChildren();
      video.removeAttribute('src');
      video.removeAttribute('poster');
      video.load(); // kaynak boşaltıldı → indirme iptal
      if (this.locked) document.body.classList.remove('overflow-hidden');
      this.locked = false;
      const opener = this.opener && this.opener.isConnected ? this.opener : null;
      this.opener = null;
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(opener);
      else if (opener) opener.focus();
    }

    onDialogClick(event) {
      const target = event.target;
      if (target === this.dialog) return this.close(); // ::backdrop tıklaması
      if (target === this.video) return this.togglePlay();
      const button = target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-close')) this.close();
      else if (button.hasAttribute('data-nav')) this.show(this.index + Number(button.dataset.nav));
      else if (button.hasAttribute('data-sound-toggle')) this.toggleSound();
      else if (button.hasAttribute('data-play-toggle')) this.togglePlay();
    }

    onKeydown(event) {
      if (this.items.length < 2 || event.altKey || event.ctrlKey || event.metaKey) return;
      const rtl = document.documentElement.dir === 'rtl';
      let step = 0;
      if (event.key === 'ArrowRight') step = rtl ? -1 : 1;
      else if (event.key === 'ArrowLeft') step = rtl ? 1 : -1;
      if (!step) return;
      event.preventDefault();
      this.show(this.index + step);
    }

    togglePlay() {
      if (this.video.paused) this.play();
      else this.video.pause();
    }

    toggleSound() {
      this.muted = !this.muted;
      this.video.muted = this.muted;
      this.syncSound();
    }

    syncSound() {
      this.dialog.classList.toggle('is-muted', this.muted);
      if (this.soundButton) this.soundButton.setAttribute('aria-pressed', String(this.muted));
    }

    syncPlay() {
      const paused = this.video.paused;
      this.dialog.classList.toggle('is-paused', paused);
      if (this.playButton) this.playButton.setAttribute('aria-label', paused ? this.dataset.play : this.dataset.pause);
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }
  }

  customElements.define('tps-video-reel', TpsVideoReel);
})();
